import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import {
  BuildingOffice2Icon,
  PlusIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronRightIcon,
  UserGroupIcon,
  CircleStackIcon,
  ChartBarIcon,
  ArrowPathIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import { TIMEZONES, TIMEZONE_GROUPS } from '../../constants/timezones';

// Industry options (same as registration)
const INDUSTRIES = [
  { value: '', label: 'Select your industry' },
  { value: 'technology', label: 'Technology' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'retail', label: 'Retail & E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'education', label: 'Education' },
  { value: 'media', label: 'Media & Entertainment' },
  { value: 'consulting', label: 'Consulting & Services' },
  { value: 'other', label: 'Other' }
];

// Format price for display
const formatPrice = (pricing, currencyCode = 'USD') => {
  const price = pricing?.find(p => p.currencyCode === currencyCode) || pricing?.[0];
  if (!price) return 'Free';
  if (price.amount === 0) return 'Free';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price.currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price.amount) + '/mo';
};

export default function Companies() {
  const navigate = useNavigate();
  const { selectedCompany, selectCompany, clearSelectedCompany, isSuperUser } = useAuthStore();
  
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    industry: '',
    timezone: 'UTC',
    billingPlan: '',
    dataLocation: ''
  });
  
  // Fetch regions and plans when modal opens
  useEffect(() => {
    if (showCreateModal) {
      loadRegions();
      loadPlans();
    }
  }, [showCreateModal]);
  
  const loadRegions = async () => {
    try {
      setLoadingRegions(true);
      const response = await apiHelpers.getAvailableRegions();
      const availableRegions = response.data.regions || [];
      setRegions(availableRegions);
      
      // Set default region if available
      const defaultRegion = availableRegions.find(r => r.isDefault);
      if (defaultRegion && !newCompany.dataLocation) {
        setNewCompany(prev => ({ ...prev, dataLocation: defaultRegion.regionId }));
      } else if (availableRegions.length > 0 && !newCompany.dataLocation) {
        setNewCompany(prev => ({ ...prev, dataLocation: availableRegions[0].regionId }));
      }
    } catch (error) {
      console.error('Failed to load regions:', error);
    } finally {
      setLoadingRegions(false);
    }
  };
  
  const loadPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await api.get('/auth/plans');
      const availablePlans = response.data.plans || [];
      setPlans(availablePlans);
      
      // Pre-select first plan if available
      if (availablePlans.length > 0 && !newCompany.billingPlan) {
        setNewCompany(prev => ({ ...prev, billingPlan: availablePlans[0].slug }));
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
      toast.error('Unable to load billing plans');
    } finally {
      setLoadingPlans(false);
    }
  };
  
  // Check if user is authorized
  useEffect(() => {
    if (!isSuperUser()) {
      navigate('/dashboards');
      toast.error('Access denied. Super Admin or Managing Admin required.');
    }
  }, [isSuperUser, navigate]);
  
  // Fetch companies
  useEffect(() => {
    fetchCompanies();
  }, []);
  
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/companies');
      setCompanies(response.data.data || response.data.companies || []);
    } catch (error) {
      console.error('Fetch companies error:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    try {
      setCreating(true);
      const response = await api.post('/companies', newCompany);
      setCompanies([response.data.company, ...companies]);
      setShowCreateModal(false);
      setNewCompany({ name: '', industry: '', timezone: 'UTC', billingPlan: '', dataLocation: '' });
      toast.success('Company created successfully');
    } catch (error) {
      console.error('Create company error:', error);
      toast.error(error.response?.data?.error || 'Failed to create company');
    } finally {
      setCreating(false);
    }
  };
  
  const handleSelectCompany = (company) => {
    selectCompany(company);
    toast.success(`Now working as ${company.name}`);
  };
  
  const handleClearSelection = () => {
    clearSelectedCompany();
    toast.success('Cleared company selection');
  };
  
  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(search.toLowerCase()) ||
    company.industry?.toLowerCase().includes(search.toLowerCase())
  );
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900 dark:text-white">
            Companies
          </h1>
          <p className="text-surface-600 dark:text-surface-400 mt-1">
            Manage and switch between companies
          </p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
        >
          <PlusIcon className="w-5 h-5" />
          New Company
        </button>
      </div>
      
      {/* Current Selection Banner */}
      {selectedCompany && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-800 flex items-center justify-center">
              <BuildingOffice2Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-primary-600 dark:text-primary-400">Currently working as</p>
              <p className="font-semibold text-primary-900 dark:text-primary-100">{selectedCompany.name}</p>
            </div>
          </div>
          <button
            onClick={handleClearSelection}
            className="btn-ghost text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"
          >
            <XCircleIcon className="w-5 h-5 mr-1" />
            Clear Selection
          </button>
        </div>
      )}
      
      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search companies..."
          className="input pl-10"
        />
      </div>
      
      {/* Companies Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="w-8 h-8 text-primary-600 animate-spin" />
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="text-center py-12">
          <BuildingOffice2Icon className="w-12 h-12 text-surface-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
            {search ? 'No companies found' : 'No companies yet'}
          </h3>
          <p className="text-surface-600 dark:text-surface-400 mb-4">
            {search ? 'Try a different search term' : 'Create your first company to get started'}
          </p>
          {!search && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              <PlusIcon className="w-5 h-5" />
              Create Company
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredCompanies.map((company) => {
            const isSelected = selectedCompany?._id === company._id;
            
            return (
              <div
                key={company._id}
                className={`card hover:shadow-lg transition-all cursor-pointer ${
                  isSelected 
                    ? 'ring-2 ring-primary-500 border-primary-500' 
                    : 'hover:border-surface-400 dark:hover:border-surface-600'
                }`}
                onClick={() => handleSelectCompany(company)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isSelected 
                        ? 'bg-primary-100 dark:bg-primary-900' 
                        : 'bg-surface-100 dark:bg-surface-800'
                    }`}>
                      {company.logo ? (
                        <img src={company.logo} alt={company.name} className="w-8 h-8 rounded" />
                      ) : (
                        <BuildingOffice2Icon className={`w-6 h-6 ${
                          isSelected 
                            ? 'text-primary-600 dark:text-primary-400' 
                            : 'text-surface-600 dark:text-surface-400'
                        }`} />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-surface-900 dark:text-white">
                        {company.name}
                      </h3>
                      {company.industry && (
                        <p className="text-sm text-surface-500 dark:text-surface-400">
                          {company.industry}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                      <CheckIcon className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="text-center p-2 bg-surface-50 dark:bg-surface-800 rounded-lg">
                    <UserGroupIcon className="w-4 h-4 text-surface-400 mx-auto mb-1" />
                    <p className="text-sm font-medium text-surface-900 dark:text-white">
                      {company.userCount || 0}
                    </p>
                    <p className="text-xs text-surface-500">Users</p>
                  </div>
                  <div className="text-center p-2 bg-surface-50 dark:bg-surface-800 rounded-lg">
                    <ChartBarIcon className="w-4 h-4 text-surface-400 mx-auto mb-1" />
                    <p className="text-sm font-medium text-surface-900 dark:text-white">
                      {company.dashboardCount || 0}
                    </p>
                    <p className="text-xs text-surface-500">Dashboards</p>
                  </div>
                  <div className="text-center p-2 bg-surface-50 dark:bg-surface-800 rounded-lg">
                    <CircleStackIcon className="w-4 h-4 text-surface-400 mx-auto mb-1" />
                    <p className="text-sm font-medium text-surface-900 dark:text-white">
                      {company.dataSourceCount || 0}
                    </p>
                    <p className="text-xs text-surface-500">Sources</p>
                  </div>
                </div>
                
                {/* Status & Plan */}
                <div className="flex items-center justify-between pt-3 border-t border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-2">
                    {company.isActive ? (
                      <span className="badge-success">
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="badge-danger">
                        <XCircleIcon className="w-3 h-3 mr-1" />
                        Inactive
                      </span>
                    )}
                    <span className="badge-primary capitalize">
                      {company.billingPlan || 'Starter'}
                    </span>
                  </div>
                  
                  <ChevronRightIcon className="w-5 h-5 text-surface-400" />
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Create Company Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-surface-900 rounded-xl w-full max-w-md shadow-xl my-8 max-h-[calc(100vh-4rem)] flex flex-col">
            <div className="p-6 border-b border-surface-200 dark:border-surface-700 flex-shrink-0">
              <h2 className="text-xl font-semibold text-surface-900 dark:text-white">
                Create New Company
              </h2>
              <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
                Set up a new company account
              </p>
            </div>
            
            <form onSubmit={handleCreateCompany} className="flex flex-col flex-1 min-h-0">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                  className="input"
                  placeholder="Acme Corporation"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  Industry
                </label>
                <select
                  value={newCompany.industry}
                  onChange={(e) => setNewCompany({ ...newCompany, industry: e.target.value })}
                  className="input"
                >
                  {INDUSTRIES.map(ind => (
                    <option key={ind.value} value={ind.value}>{ind.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Timezone */}
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  <ClockIcon className="w-4 h-4 inline mr-1" />
                  Timezone *
                </label>
                <select
                  value={newCompany.timezone}
                  onChange={(e) => setNewCompany({ ...newCompany, timezone: e.target.value })}
                  className="input"
                  required
                >
                  {TIMEZONE_GROUPS.map(group => (
                    <optgroup key={group} label={group}>
                      {TIMEZONES.filter(tz => tz.group === group).map(tz => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <p className="text-xs text-surface-500 dark:text-surface-400 mt-1">
                  This timezone will be used for AI responses and date/time formatting
                </p>
              </div>
              
              {/* Data Warehouse Region */}
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  <GlobeAltIcon className="w-4 h-4 inline mr-1" />
                  Data Warehouse Region *
                </label>
                {loadingRegions ? (
                  <div className="input flex items-center justify-center">
                    <ArrowPathIcon className="w-5 h-5 animate-spin text-primary-500" />
                  </div>
                ) : regions.length === 0 ? (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-sm text-amber-700 dark:text-amber-400">
                    <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" />
                    No regions available. Contact your administrator.
                  </div>
                ) : (
                  <>
                    <select
                      value={newCompany.dataLocation}
                      onChange={(e) => setNewCompany({ ...newCompany, dataLocation: e.target.value })}
                      className="input"
                      required
                    >
                      {regions.map((region) => (
                        <option key={region.regionId} value={region.regionId}>
                          {region.flag} {region.name} ({region.regionId})
                          {region.isDefault ? ' - Default' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-surface-500 dark:text-surface-400 mt-1">
                      <ExclamationTriangleIcon className="w-3 h-3 inline mr-1" />
                      Region cannot be changed after company creation
                    </p>
                  </>
                )}
              </div>
              
              {/* Billing Plan */}
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Billing Plan *
                </label>
                {loadingPlans ? (
                  <div className="flex items-center justify-center py-4">
                    <ArrowPathIcon className="w-5 h-5 animate-spin text-primary-500" />
                  </div>
                ) : plans.length === 0 ? (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-sm text-amber-700 dark:text-amber-400">
                    <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" />
                    No billing plans available. Contact your administrator.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {plans.map((plan) => {
                      const features = plan.features || {};
                      return (
                        <label
                          key={plan.slug}
                          className={`block p-3 border rounded-lg cursor-pointer transition-colors ${
                            newCompany.billingPlan === plan.slug
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-surface-200 dark:border-surface-700 hover:border-surface-400'
                          }`}
                        >
                          <input
                            type="radio"
                            name="billingPlan"
                            value={plan.slug}
                            checked={newCompany.billingPlan === plan.slug}
                            onChange={(e) => setNewCompany({ ...newCompany, billingPlan: e.target.value })}
                            className="sr-only"
                          />
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center flex-shrink-0 ${
                                newCompany.billingPlan === plan.slug
                                  ? 'border-primary-500'
                                  : 'border-surface-400'
                              }`}>
                                {newCompany.billingPlan === plan.slug && (
                                  <div className="w-2 h-2 rounded-full bg-primary-500" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="font-semibold text-surface-900 dark:text-white">{plan.name}</p>
                                  <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                                    {formatPrice(plan.pricing)}
                                  </p>
                                </div>
                                <ul className="text-xs text-surface-600 dark:text-surface-400 space-y-0.5">
                                  <li>• {features.userSeats || 1} user seat{(features.userSeats || 1) > 1 ? 's' : ''}</li>
                                  <li>• {features.storageGB || 0} GB storage</li>
                                  <li>• {features.dashboardLimit === -1 
                                    ? 'Unlimited dashboards' 
                                    : `${features.dashboardLimit || 1} dashboard${(features.dashboardLimit || 1) > 1 ? 's' : ''}`}
                                  </li>
                                  {(features.monthlyCredits > 0 || features.oneTimeCredits > 0) && (
                                    <li>• {features.monthlyCredits > 0 
                                      ? `${features.monthlyCredits.toLocaleString()} AI credits/mo`
                                      : `${(features.oneTimeCredits || 0).toLocaleString()} AI credits`}
                                    </li>
                                  )}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              </div>
              
              {/* Fixed Footer with Buttons */}
              <div className="p-6 border-t border-surface-200 dark:border-surface-700 flex gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewCompany({ name: '', industry: '', timezone: 'UTC', billingPlan: '', dataLocation: '' });
                  }}
                  className="btn-secondary flex-1"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="w-5 h-5" />
                      Create Company
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

